import { useMemo } from 'react';
import type { TrackingEvent } from '@archipro-website/tracker';
import { useTracker } from '@archipro-website/tracker';
import { useSharedTiles } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { getSourceSection } from '~/utils/get-source-section';
import {
    sectionNamesItsOwnEvent,
    tileClickEventForSection,
} from '~/ui-rocco/hooks/tile-click-event-for-section';
import { useSetItemBanner } from '~/modules/directory/hook/use-set-item-banner';
import { useFeature } from '~/modules/root';
import { PROJECT_TILE_BANNER_SELECTOR_FLAG } from '~/modules/directory/hook/use-set-item-banner';
import { isUserLoggedIn, useUser } from '~/modules/user/hook/use-user';

interface UseCategoryTilesTrackingOptions {
    /** When false, skip injecting tile actions (parent owns tracking). */
    enabled?: boolean;
}

export const useCategoryTilesTracking = (
    gridRef: React.RefObject<HTMLElement | null>,
    options: UseCategoryTilesTrackingOptions = {},
) => {
    const { enabled = true } = options;
    const tracker = useTracker();
    const setItemBanner = useSetItemBanner();
    const user = useUser();
    const isHighAdmin =
        isUserLoggedIn(user) &&
        'IsHighAdmin' in user &&
        user.IsHighAdmin === true;
    // Gate the set-as-banner action behind the feature flag and high-admin
    // rollout (BC-4449 project slider is high-admin-only for now).
    const bannerSelectorEnabled = useFeature(PROJECT_TILE_BANNER_SELECTOR_FLAG);
    const onSetBanner =
        bannerSelectorEnabled && isHighAdmin ? setItemBanner : undefined;

    /**
     * Memoised because `useSharedTiles` keys a layout effect on this object's
     * identity: a fresh literal every render rewrites the shared tile-actions
     * atom on every render, re-rendering every tile subscribed to it. The
     * sibling `use-search-tile-click-tracking` memoises for the same reason.
     */
    const trackingConfig = useMemo(
        () => ({
            onClick: (
                targetRef: React.RefObject<HTMLElement | null>,
                itemID?: number,
                professionalID?: number,
                section?: string,
            ) => {
                const logEvent = (eventName: TrackingEvent) => {
                    tracker.log(eventName, {
                        url: new URL(window.location.href),
                        data: {
                            LibraryItemID: itemID,
                            ProfessionalID: professionalID,
                        },
                    });
                };

                const named = tileClickEventForSection(section);
                if (named) {
                    logEvent(named);
                    return;
                }
                // A rail that names itself and is deliberately silent stays silent
                // rather than falling through to the generic click.
                if (sectionNamesItsOwnEvent(section)) {
                    return;
                }
                // The position is best-effort. It needs the grid element, and a
                // host that does not attach the ref leaves getGridPosition
                // returning undefined — which used to drop the whole event
                // silently, so a tile click on such a grid was reported as no
                // click at all rather than as a click with no position. The click
                // is the signal; the position is a detail nothing currently reads.
                const gridData = getGridPosition(gridRef, targetRef);
                tracker.log('directoryTileClick', {
                    url: new URL(window.location.href),
                    data: {
                        LibraryItemID: itemID,
                        ProfessionalID: professionalID,
                        ...(gridData && {
                            // format: Row,Column,CountPerRow,AbsolutePosition
                            ExtraData: `${gridData.row},${gridData.column},${gridData.columnsPerRow},${gridData.position}`,
                        }),
                    },
                });
            },

            onHover: (
                targetRef: React.RefObject<HTMLElement | null>,
                itemID?: number,
                professionalID?: number,
            ) => {
                const gridData = getGridPosition(gridRef, targetRef);
                tracker.log('DirectoryTileHover', {
                    url: new URL(window.location.href),
                    targetTracker: 'archiproTracker',
                    data: {
                        LibraryItemID: itemID,
                        ProfessionalID: professionalID,
                        ...(gridData && {
                            ExtraData: `${gridData.row},${gridData.column},${gridData.columnsPerRow},${gridData.position}`,
                        }),
                    },
                });
            },

            onTileMediaSliderSlideChange: (
                direction: 'CarouselPrev' | 'CarouselNext',
                itemID?: number,
            ) => {
                const sectionSource = getSourceSection();
                tracker.log(direction, {
                    url: new URL(window.location.href),
                    data: {
                        Source: sectionSource,
                        ExtraData: {
                            libraryItemId: itemID,
                        },
                    },
                });
            },
        }),
        [tracker, gridRef, setItemBanner],
    );

    const sharedConfig = useMemo(
        () => (enabled ? { ...trackingConfig, onSetBanner } : undefined),
        [enabled, trackingConfig, onSetBanner],
    );

    useSharedTiles({ trackingConfig: sharedConfig });
};

/**
 * Calculates the position of an element within a CSS Grid layout.
 *
 * This function determines:
 * - The row and column position of the target element
 * - The total number of columns in the grid
 * - The absolute position of the element in the grid sequence
 *
 * The calculation takes into account:
 * - Grid column and row gaps
 * - The relative position of the target element to the grid container
 * - The dimensions of both the grid and target element
 *
 * @param {React.RefObject<HTMLElement | null>} gridRef - Reference to the grid container element
 * @param {React.RefObject<HTMLElement | null>} targetRef - Reference to the target element within the grid
 * @returns {Object | undefined} Position data containing:
 *   - column: The column number (1-based)
 *   - row: The row number (1-based)
 *   - columnsPerRow: Total number of columns in the grid
 *   - position: Absolute position in the grid sequence (1-based)
 *   Returns undefined if either reference is invalid
 */
const getGridPosition = (
    gridRef: React.RefObject<HTMLElement | null>,
    targetRef: React.RefObject<HTMLElement | null>,
) => {
    const gridElement = gridRef.current;
    const element = targetRef.current;

    if (!element || !gridElement) return;

    const gridStyles = window.getComputedStyle(gridElement);

    // Get grid properties
    const gridColumns = gridStyles.gridTemplateColumns.split(' ').length;
    const gridColumnGap = parseInt(gridStyles.columnGap) || 0;
    const gridRowGap = parseInt(gridStyles.rowGap) || 0;

    // Get element bounds
    const elementBounds = element.getBoundingClientRect();
    const gridBounds = gridElement.getBoundingClientRect();

    // Calculate cell size (including gap)
    const cellWidth = (gridBounds.width + gridColumnGap) / gridColumns;
    const cellHeight =
        (gridBounds.height + gridRowGap) / (gridBounds.height / cellWidth);

    // Calculate position
    const relativeLeft = elementBounds.left - gridBounds.left;
    const relativeTop = elementBounds.top - gridBounds.top;

    const column = Math.floor(relativeLeft / cellWidth) + 1;
    const row = Math.floor(relativeTop / cellHeight) + 1;

    return {
        column,
        row,
        columnsPerRow: gridColumns,
        position: gridColumns * (row - 1) + column,
    };
};
